/**
 * Ingestion change-set review state over the Phase 11 REST API.
 *
 * Loads one ingestion run's change set (each with a current-vs-proposed section
 * diff), adjudicates a single change (confirm / reject / edit), and batch-
 * confirms the whole run. Drives the {@link ProposalDiffReview} UI; the run is
 * surfaced from the notification flow Phase 9 already wired. Proposed content is
 * PHI — shown for review, never logged.
 */
import { useCallback, useEffect, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";
import type {
  IngestionChange,
  IngestionChangeStatus,
  IngestionRunDetail,
  SyncBackResolution,
} from "../types";

/** Strip the fetch client's "API error! status: NNN - " prefix for display. */
function cleanError(message: string): string {
  return message.replace(/^API error!\s*status:\s*\d+\s*-\s*/i, "").trim();
}

const RUNS_ENDPOINT = "/api/ingestion/runs";
const CHANGES_ENDPOINT = "/api/ingestion/changes";

/** The lifecycle status of a review load. */
export type ReviewStatus = "loading" | "loaded" | "error";

/** The result surface returned by {@link useIngestionReview}. */
export interface UseIngestionReviewResult {
  /** The current load status. */
  status: ReviewStatus;
  /** The loaded run + its changes, once available. */
  detail: IngestionRunDetail | null;
  /**
   * Adjudicate one change. For a confirmed sync-back conflict, pass
   * ``resolution`` (`"replace"`/`"blend"`) — the backend re-applies the pick onto
   * the live card. After a successful adjudicate the run is re-fetched; resolves
   * to `true` only when the run is now fully resolved (no change left pending) so
   * the caller can fire its resolved callback (e.g. refresh the pending-review
   * bell). Resolves `false` when changes remain pending, or when the call errored
   * (e.g. a 409: the card moved/retired since proposed — surfaced via
   * {@link error}).
   */
  adjudicate: (
    changeId: string,
    decision: IngestionChangeStatus,
    adjusted?: Record<string, string>,
    resolution?: SyncBackResolution,
  ) => Promise<boolean>;
  /**
   * Batch-confirm every pending non-conflict change in the run. Resolves to
   * `true` only when the run is now fully resolved (``confirmed``); `false` when
   * conflicts or a skipped-stale change remain pending (the panel stays open), or
   * the call errored (surfaced via {@link error}).
   */
  confirmAll: () => Promise<boolean>;
  /**
   * A plain-language error from the last adjudicate/confirm (e.g. a 409 "card
   * changed since proposed; re-run sync-back"), or `null`. The panel surfaces
   * this inline rather than failing silently (ADR-011 fail-loud).
   */
  error: string | null;
  /** Re-fetch the run. */
  reload: () => void;
}

/**
 * Load and adjudicate an ingestion run's change set.
 *
 * @param runId - The ingestion run to review (empty string disables the hook).
 * @returns The review status, detail, and adjudicate/confirm/reload actions.
 */
export function useIngestionReview(runId: string): UseIngestionReviewResult {
  const [status, setStatus] = useState<ReviewStatus>("loading");
  const [detail, setDetail] = useState<IngestionRunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState(0);

  const reload = useCallback(() => setToken((t) => t + 1), []);

  const fetchRunDetail =
    useCallback(async (): Promise<IngestionRunDetail | null> => {
      const resp = await apiUtils.fetchData<IngestionRunDetail>(
        `${RUNS_ENDPOINT}/${encodeURIComponent(runId)}`,
      );
      return isApiError(resp) ? null : resp;
    }, [runId]);

  useEffect(() => {
    if (runId === "") return undefined;
    let active = true;

    async function load(): Promise<void> {
      setStatus("loading");
      const resp = await fetchRunDetail();
      if (!active) return;
      if (resp === null) {
        setStatus("error");
        return;
      }
      setDetail(resp);
      setStatus("loaded");
    }

    load().catch((err: unknown) => {
      if (!active) return;
      logger.devOnly.warn("useIngestionReview: load failed", {
        error: String(err),
      });
      setStatus("error");
    });

    return () => {
      active = false;
    };
  }, [runId, token, fetchRunDetail]);

  const adjudicate = useCallback(
    async (
      changeId: string,
      decision: IngestionChangeStatus,
      adjusted?: Record<string, string>,
      resolution?: SyncBackResolution,
    ): Promise<boolean> => {
      setError(null);
      const resp = await apiUtils.fetchData<IngestionChange>(
        `${CHANGES_ENDPOINT}/${encodeURIComponent(changeId)}/adjudicate`,
        "POST",
        {
          decision,
          adjusted_content: adjusted ?? null,
          resolution: resolution ?? null,
        },
      );
      if (isApiError(resp)) {
        logger.devOnly.warn("useIngestionReview: adjudicate failed");
        setError(cleanError(resp.error));
        return false;
      }
      // Decide "run resolved" from the reloaded run state, not the single change:
      // the run is terminal only once no change is left pending. Bulk "Confirm all"
      // deliberately skips conflicts, so an individual keep/replace/blend pick is
      // the only path that resolves a conflict-containing run — fire the resolved
      // callback here too, else the pending-review bell would stay stale (ADR-011).
      const refreshed = await fetchRunDetail();
      if (refreshed !== null) setDetail(refreshed);
      return (
        refreshed !== null &&
        refreshed.changes.every((c) => c.status !== "pending")
      );
    },
    [fetchRunDetail],
  );

  const confirmAll = useCallback(async (): Promise<boolean> => {
    setError(null);
    const resp = await apiUtils.fetchData<{ status?: string }>(
      `${RUNS_ENDPOINT}/${encodeURIComponent(runId)}/confirm`,
      "POST",
    );
    if (isApiError(resp)) {
      logger.devOnly.warn("useIngestionReview: confirm failed");
      setError(cleanError(resp.error));
      return false;
    }
    reload();
    // ``confirmed`` = every change resolved; ``partially_adjudicated`` = conflicts
    // (or a stale change the backend skipped) remain pending — keep the panel open.
    return resp.status === "confirmed";
  }, [runId, reload]);

  return { status, detail, adjudicate, confirmAll, error, reload };
}
